import React from 'react';
import supercluster from 'supercluster';
import find from 'lodash/find';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import { gettext } from "@eb/i18n";
import { getWindowObject } from '@eb/feature-detection';

import InteractiveMapMarker from './interactiveMapMarker/InteractiveMapMarker';

import {
    DEFAULT_ZOOM_VALUE,
    MAX_ZOOM,
    GLOBAL_BBOX,
    SEARCH_MAP_BASE_PROPTYPES,
    MOBILE_MAP_FULL_SCREEN,
    MAX_ZOOM_WHEN_SEARCHING,
} from './constants';
import {
    calculateAverageCenter,
    convertEventsToGeoJSON,
    getLatLngsFromEvents,
} from './utils';

import './searchMap.scss';

const withSearchMapBase = (SearchMapVariant) =>
    (class SearchMap extends React.PureComponent {
        static propTypes = SEARCH_MAP_BASE_PROPTYPES;

        static defaultProps = {
            events: [],
            height: 400,
            width: '100%',
            enterMapView: noop,
        };

        constructor(props) {
            super(props);

            this.clusterIndex = supercluster({
                radius: 5,
                maxZoom: MAX_ZOOM,
            });

            this.clusterIndex.load(convertEventsToGeoJSON(props.events));

            const zoom = DEFAULT_ZOOM_VALUE;

            let center = {
                lat: props.latitude || 0,
                lng: props.longitude || 0,
            };

            if (!props.latitude || !props.longitude) {
                center = calculateAverageCenter(
                    this.clusterIndex.getClusters(GLOBAL_BBOX, zoom),
                    center,
                );
            }

            this.state = {
                zoom,
                bounds: {
                    // Note that by the time the user sees anything,
                    // these will already be set to the correct bounds
                    // because _handleBoundsChange gets automatically
                    // called once by the library during the initial mount
                    ne: { lat: 90, lng: 180 },
                    nw: { lat: 90, lng: -180 },
                    se: { lat: -90, lng: 180 },
                    sw: { lat: -90, lng: -180 },
                },
                mapBoundsChangeCount: 0,
                center,
                tooltipSelectedEventsIds: [],
                markerRef: null,
                isMobileViewEventDefaultSelected: false,
            };
        }

        componentDidMount() {
            const { setShouldScroll, events } = this.props;

            if (setShouldScroll) {
                setShouldScroll(events.length);
            }

            const addEventListener = getWindowObject('addEventListener');

            if (addEventListener) {
                addEventListener('resize', this._debounceRefitMap);
            }
        }

        // eslint-disable-next-line
        UNSAFE_componentWillReceiveProps(newProps) {
            this.clusterIndex.load(convertEventsToGeoJSON(newProps.events));

            // Check to make sure the previous props received were empty,
            // because this component receives an empty events array a few times
            // during the wait time for a search to complete.
            // We don't want to keep setting the center during that time
            // because it causes the map to jump around during the
            // old events array -> empty array -> new events array lifecycle
            if (!this.props.events.length && newProps.events.length) {
                if (this.smartZoom) {
                    this.smartZoom(newProps.events);
                }
            }

            // If a new search is run from within the mobile full screen view OR
            // the full screen view is instantiated, we want to default select the first returned event cluster
            if (
                (!this.props.events.length &&
                    newProps.events.length &&
                    this._isMobileFullScreen(newProps)) ||
                (this._isMobileFullScreen(newProps) &&
                    !this._isMobileFullScreen())
            ) {
                this.setState({
                    isMobileViewEventDefaultSelected: true,
                    tooltipSelectedEventsIds: this._getFirstEventClusterEventIds(),
                });
            }

            // Whenever the location is change this function will be executed
            // if we don't have any event and we're not searching for boundaries
            // we need to center the map using the passed in lat and long
            if (
                !newProps.isLoading &&
                !newProps.searchMapBounds &&
                !newProps.events.length &&
                newProps.latitude &&
                newProps.longitude
            ) {
                const center = {
                    lat: newProps.latitude,
                    lng: newProps.longitude,
                };

                this.setState({
                    center,
                    zoom: DEFAULT_ZOOM_VALUE,
                });
            }

            if (this.props.setShouldScroll) {
                this.props.setShouldScroll(
                    newProps.events.length || newProps.isLoading,
                );
            }
        }

        componentWillUnmount() {
            const removeEventListener = getWindowObject('removeEventListener');

            if (removeEventListener) {
                removeEventListener('resize', this._debounceRefitMap);
            }
        }

        _handleBoundsChange = ({ zoom, bounds }) => {
            // This variable is intended to track when the map bounds are changed at the same zoom level,
            // so we only increment if the zoom level stays the same.
            // eslint-disable-next-line react/no-access-state-in-setstate
            const mapBoundsChangeCount = this.state.mapBoundsChangeCount + 1;

            this.setState({
                zoom,
                bounds,
                mapBoundsChangeCount,
            });
        };

        _handleSearchByBounds = () => {
            this.props.searchByBBox(this.state.bounds);
        };

        _handleMapMarkerClick = (eventsId) => (markerRef) => {
            this.setState({
                tooltipSelectedEventsIds: eventsId,
                markerRef,
            });
        };

        _handleClickOutsideMapMarker = () => {
            this.setState({
                tooltipSelectedEventsIds: [],
                markerRef: null,
                isMobileViewEventDefaultSelected: false,
            });
        };

        _isMobileFullScreen = (passedProps) => {
            if (passedProps === undefined) {
                return this.props.location.hash === MOBILE_MAP_FULL_SCREEN;
            }

            return passedProps.location.hash === MOBILE_MAP_FULL_SCREEN;
        };

        _toggleMobileView = (hash = '') => {
            const { history } = this.props;

            const locationObject = {
                ...history.location,
                hash,
            };

            history.push(locationObject);
        };

        _handleMobileMapClick = () => {
            this._toggleMobileView(MOBILE_MAP_FULL_SCREEN);

            this.props.enterMapView();
        };

        _handleMobileMapClose = () => {
            this._toggleMobileView();
        };

        _getEventIdFromEventProps = (eventsId) => {
            let selectedId;

            if (!isEmpty(eventsId)) {
                const id = first(eventsId);

                selectedId = id;
            }

            return selectedId;
        };

        _getFirstEventClusterEventIds = () => {
            let firstEventCluster;
            const markerData = this._getEventMarkerData();

            if (markerData.length) {
                firstEventCluster = first(markerData);
            }

            return firstEventCluster && firstEventCluster.eventsId;
        };

        _getEventMarkerData = () => {
            // Turns an array of single points and clusters into an array of map markers.
            // Clusters represent groups of events that are very close to each other on the map,
            // which is determined by pixel radius.
            const {
                bounds,
                zoom,
                isMobileViewEventDefaultSelected,
                tooltipSelectedEventsIds,
            } = this.state;
            const {
                hoverEventCardTarget,
                onSaveEvent,
                isAuthenticated,
                isFixed,
                isLoading,
            } = this.props;

            let clusters = this.clusterIndex.getClusters(
                [bounds.sw.lng, bounds.sw.lat, bounds.se.lng, bounds.ne.lat],
                zoom,
            );

            // sort by latitude so that the markers look orderly on the map
            clusters = clusters.sort(
                (a, b) => b.geometry.coordinates[1] - a.geometry.coordinates[1],
            );

            return clusters.map(({ geometry, properties }, index) => {
                const lng = parseFloat(geometry.coordinates[0]);
                const lat = parseFloat(geometry.coordinates[1]);

                let markerProps = {
                    lat,
                    lng,
                    label: properties.id,
                    name: properties.name,
                    hoverEventCardTarget,
                    onSaveEvent,
                    isAuthenticated,
                    parentIsFixed: isFixed,
                };

                let eventProps = [properties];

                if (properties && properties.cluster) {
                    let clusterName = gettext(
                        'Zoom to see %(eventCount)s events',
                        { eventCount: properties.point_count },
                    );
                    const clusterChildren = this.clusterIndex.getLeaves(
                        properties.cluster_id,
                        20,
                    );

                    if (properties.point_count === 2 || zoom === MAX_ZOOM) {
                        // In some cases, for example when points share the same address (like when they're at the same hotel),
                        // the clusters will never be split up into single points. Therefore, we need some logic to
                        // ensure that the user can still see the names.

                        clusterName = clusterChildren
                            .map(
                                ({ properties: childProperties }) =>
                                    childProperties.name,
                            )
                            .join(' & ');
                    }

                    markerProps = {
                        ...markerProps,
                        label: properties.id,
                        name: clusterName,
                    };

                    eventProps = clusterChildren.map(
                        (clusterChild) => clusterChild.properties,
                    );
                }

                const eventsId = eventProps.map(({ id }) => id);

                const _handleMapMarkerClick = this._handleMapMarkerClick(
                    eventsId,
                );

                /* Do necessary logic to properly highlight and display appropriate tooltip
                for the fullscreen map mode */
                if (this._isMobileFullScreen()) {
                    let fullScreenViewProps = {};
                    const shouldSelectDefaultCluster =
                        isMobileViewEventDefaultSelected && index === 0;
                    const shouldSelectCluster = find(
                        tooltipSelectedEventsIds,
                        (eventId) =>
                            this._getEventIdFromEventProps(eventsId) ===
                            eventId,
                    );

                    let mobileHoverTarget;

                    if (shouldSelectDefaultCluster || shouldSelectCluster) {
                        mobileHoverTarget = this._getEventIdFromEventProps(
                            eventsId,
                        );
                    }

                    fullScreenViewProps = {
                        isMobileViewEventDefaultSelected,
                        mobileViewHoverTarget: isLoading
                            ? ''
                            : mobileHoverTarget,
                    };

                    markerProps = {
                        ...markerProps,
                        ...fullScreenViewProps,
                    };
                }

                return {
                    ...markerProps,
                    eventsId,
                    key: properties.id || `cluster-${properties.cluster_id}`,
                    onMapMarkerClick: _handleMapMarkerClick,
                };
            });
        };

        _getEventMarkers = () =>
            this._getEventMarkerData().map((markerData) => (
                //eslint-disable-next-line
                <InteractiveMapMarker {...markerData} />
            ));

        _createSmartZoom = (map, maps) => (events) => {
            // don't change the bounds if no events
            if (!events || !events.length) {
                return;
            }

            const bounds = new maps.LatLngBounds();
            const latLngs = getLatLngsFromEvents(events);

            latLngs.forEach((latLng) => bounds.extend(new maps.Marker(latLng)));
            const originalMaxZoom = map.maxZoom;
            map.setOptions({ maxZoom: MAX_ZOOM_WHEN_SEARCHING });
            map.fitBounds(bounds);
            map.setOptions({ maxZoom: originalMaxZoom });
        };

        _refitMap = () => {
            const { events } = this.props;

            if (this.smartZoom) {
                this.smartZoom(events);
            }
        };

        _debounceRefitMap = debounce(this._refitMap, 500);

        _handleGoogleApiLoaded = ({ map, maps }) => {
            this.smartZoom = this._createSmartZoom(map, maps);

            this.smartZoom(this.props.events);
        };

        render() {
            const {
                height,
                width,
                countryCode,
                googleMapLanguage,
                events,
            } = this.props;

            const { zoom, center } = this.state;

            let markers;

            if (events.length) {
                markers = this._getEventMarkers();
            }

            // For passing to the actual EDS Interactive Map component
            // (just for organizational purposes)
            const propsForInteractiveMap = {
                height,
                width,
                language: googleMapLanguage,
                region: countryCode,
                center,
                zoom,
                onChange: this._handleBoundsChange,
                onGoogleApiLoaded: this._handleGoogleApiLoaded,
                yesIWantToUseGoogleMapApiInternals: true,
            };

            return (
                <SearchMapVariant
                    {...this.props}
                    {...this.state}
                    onSearchByBounds={this._handleSearchByBounds}
                    onClickOutsideMapMarker={this._handleClickOutsideMapMarker}
                    onMapClick={this._handleMobileMapClick}
                    onBackButtonClick={this._handleMobileMapClose}
                    markers={markers}
                    propsForInteractiveMap={propsForInteractiveMap}
                />
            );
        }
    });

export default withSearchMapBase;
